import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import CustomInput from "./CustomInput";
import CustomButton from "./CustomButton";

const ForgotPasswordModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0); // Start with 0 to not show resend immediately
  const [errorMessage, setErrorMessage] = useState({
    phone: "",
    password: "",
    otp: "",
  });
  const [otpSent, setOtpSent] = useState(false); // Track if OTP has been sent

  useEffect(() => {
    let interval;
    if (step === 1 && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  useEffect(() => {
    return () => {
      setPhone("");
      setOtp("");
      setPassword("");
      setConfirmPassword("");
      setStep(1);
      setTimer(0);
      setErrorMessage({ phone: "", otp: "", password: "" });
      setOtpSent(false);
    };
  }, []);

  // Auto-send OTP when phone reaches 10 digits and OTP hasn't been sent yet
  useEffect(() => {
    if (phone.length === 10 && !otpSent) {
      sendOTP();
    }
  }, [phone, otpSent]);

  const sendOTP = async () => {
    try {
      setLoading(true);
      await axios.post(`/api/sendOtp?phone=${phone}&pushToken=${null}`);
      toast.success("OTP sent to +91" + phone);
      setTimer(30); // Start the 30-second timer
      setOtpSent(true); // Mark OTP as sent
    } catch {
      toast.error("Failed to send OTP");
      setOtpSent(false); // Allow retry if failed
    } finally {
      setLoading(false);
    }
  };

  const handleStepOneContinue = () => {
    if (phone.length !== 10 || otp.length !== 6) {
      toast.error("Enter valid phone and OTP");
      return;
    }
    setStep(2);
  };

  const checkPassword = (password, conPassword) => {
    const regPassword =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_+()<>,./:";'{}])(?=.{8,})/;
    if (regPassword.test(password) && password === conPassword) {
      return true;
    } else {
      setPassword("");
      setConfirmPassword("");
      return false;
    }
  };

  const checkPhone = (phone) => {
    return phone.length === 10;
  };

  const validate = () => {
    setLoading(true);
    let newErrors = {};

    newErrors.password =
      password.length !== 0
        ? password === confirmPassword
          ? checkPassword(password, confirmPassword)
            ? ""
            : "Use a strong password"
          : "Passwords do not match"
        : "Please enter required fields";

    newErrors.phone =
      phone.length !== 0
        ? checkPhone(phone)
          ? ""
          : "Invalid Phone Number"
        : "Please enter required fields";

    newErrors.otp = otp.length !== 0 ? "" : "Please enter required fields";

    setErrorMessage({ ...newErrors });
    setLoading(false);

    return Object.values(newErrors).every((v) => v === "");
  };

  const handleResetPassword = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      await axios.post(
        `/api/changePassword?phone=${phone}&otp=${otp}&password=${password}`
      );
      toast.success("Password changed successfully");
      onClose();
    } catch {
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 p-4  z-50 bg-[rgba(0,0,0,0.3)] flex justify-center items-center"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4">
          {step === 1 ? "Forgot Password" : "Set New Password"}
        </h2>

        {step === 1 && (
          <div className="space-y-4">
            <CustomInput
              id="forgot-phone"
              label="Phone Number"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/, ""))}
              placeholder="Enter phone number"
              minLength={10}
              maxLength={10}
              required
              error={errorMessage.phone}
            />
            <CustomInput
              id="otp"
              label="OTP"
              type="tel"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/, ""))}
              placeholder="Enter OTP"
              maxLength={6}
              required
              error={errorMessage.otp}
            />
            {timer === 0 && otpSent ? (
              <button
                type="button"
                onClick={sendOTP}
                className="text-sm text-blue-600"
              >
                Resend OTP
              </button>
            ) : timer > 0 ? (
              <p className="text-sm text-gray-500">Resend OTP in {timer}s</p>
            ) : null}
            <CustomButton
              title="Continue"
              onPress={handleStepOneContinue}
              disabled={loading}
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <CustomInput
              id="new-password"
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              showToggle
              error={errorMessage.password}
            />
            <CustomInput
              id="confirm-password"
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              required
              showToggle
            />
            <CustomButton
              title={loading ? "Setting..." : "Set Password"}
              onPress={handleResetPassword}
              disabled={loading}
            />
          </div>
        )}
        <button
          className="absolute top-2 right-3 text-gray-500 text-xl"
          onClick={onClose}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
